<template>
  <div>
    <MediaGrid
      :media="media"
      :selected-ids="selectedIds"
      :badges="badges"
      :loading="loading"
      :selectable="true"
      :zoomable="zoomable"
      mode="gallery"
      @select="handleSelect"
      @zoom="handleZoom"
    >
      <template #loading-text>
        <translate translate-context="edge-prints.job-media">Loading photos...</translate>
      </template>
      <template #empty-text>
        <translate translate-context="edge-prints.job-media">No photos in this print job</translate>
      </template>
    </MediaGrid>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import MediaGrid from '@/domain/core/components/media/MediaGrid.vue'

export default {
  name: 'PrintJobMediaPreview',
  components: {
    MediaGrid
  },
  props: {
    printJob: {
      type: Object,
      required: true
    },
    zoomable: {
      type: Boolean,
      default: true
    },
    mode: {
      type: String,
      default: 'preview',
      validator: val => ['preview', 'gallery'].includes(val)
    },
    customBadges: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      loading: false
    }
  },

  computed: {
    ...mapGetters('edgePrints', [
      'getPrintJobPhotos',
      'getPrintJobSelectedIds',
      'currentOrder'
    ]),

    media() {
      return this.getPrintJobPhotos(this.printJob.id) || []
    },

    selectedIds() {
      return this.getPrintJobSelectedIds(this.printJob.id)
    },

    badges() {
      // Use custom badges if provided, otherwise no badges
      return this.customBadges || {}
    },

    allSelected() {
      return this.media.length > 0 && this.selectedIds.length === this.media.length
    }
  },

  methods: {
    ...mapActions('edgePrints', [
      'fetchPrintJobPhotos'
    ]),

    handleSelect({ event, mediaId }) {
      const currentSelection = this.getPrintJobSelectedIds(this.printJob.id)
      const index = currentSelection.indexOf(mediaId)

      let newSelection
      if (index > -1) {
        newSelection = currentSelection.filter(id => id !== mediaId)
      } else {
        newSelection = [...currentSelection, mediaId]
      }

      this.$store.commit('edgePrints/setPrintJobMediaSelection', {
        printJobId: this.printJob.id,
        selectedIds: newSelection
      })
    },

    handleZoom(media) {
      this.$emit('zoom', media)
    },

    toggleSelectAll() {
      if (this.allSelected) {
        this.$store.commit('edgePrints/setPrintJobMediaSelection', {
          printJobId: this.printJob.id,
          selectedIds: []
        })
      } else {
        this.$store.commit('edgePrints/setPrintJobMediaSelection', {
          printJobId: this.printJob.id,
          selectedIds: this.media.map(m => m.id)
        })
      }
    },

    async loadPhotos() {
      if (!this.printJob.id || this.media.length > 0 || this.loading) return

      this.loading = true
      try {
        await this.fetchPrintJobPhotos(this.printJob)
      } catch (error) {
        console.error('Failed to load print job photos:', error)
      } finally {
        this.loading = false
      }
    }
  },

  async mounted() {
    await this.loadPhotos()
  }
}
</script>
